// Share report dialog component

import { useState, useMemo, useEffect } from 'react';
import {
  VStack,
  Button,
  Checkbox,
  CheckboxGroup,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Text,
  HStack,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { MdSearch } from "react-icons/md";
import { Modal } from '@/design-system/components/Modal';
import { useDebounce } from '@/client/hooks/useDebounce';
import { EmptyState } from '@/design-system/components/EmptyState';
import { TeamMember } from '@/lib/sdk/team/types';
import { Report } from '@/lib/sdk/reports/types';
import { useReportAccess } from '@/lib/sdk/reports/queries';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  report: Report | null;
  teamMembers: TeamMember[];
  onShare: (reportId: string, usersToAdd: string[], usersToRevoke: string[]) => void;
  isSharing?: boolean;
}

/**
 * Dialog for sharing reports with team members
 */
export function ShareDialog({
  isOpen,
  onClose,
  report,
  teamMembers,
  onShare,
  isSharing = false,
}: ShareDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Fetch existing access list
  const { data: accessData, isLoading: isLoadingAccess } = useReportAccess(report?.id || '');
  const existingAccess = accessData?.items || [];

  // Initialize selectedUsers with existing access when dialog opens
  useEffect(() => {
    if (isOpen && report && existingAccess.length > 0) {
      const initialSelection = existingAccess.map(access =>
        `${access.user_id}_${access.country_code}_${access.phone_number}`
      );
      setSelectedUsers(initialSelection);
    } else if (isOpen && report) {
      setSelectedUsers([]);
    }
  }, [isOpen, report, existingAccess]);

  const filteredMembers = useMemo(() => {
    if (!debouncedSearch) return teamMembers;

    const search = debouncedSearch.toLowerCase();
    return teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(search) ||
        member.phone_number.includes(search)
    );
  }, [teamMembers, debouncedSearch]);

  // Calculate if there are changes to save
  const hasChanges = useMemo(() => {
    if (!existingAccess.length && selectedUsers.length === 0) return false;

    const existingUserIds = new Set(
      existingAccess.map(access => `${access.user_id}_${access.country_code}_${access.phone_number}`)
    );
    const selectedUserIds = new Set(selectedUsers);

    // Check if there are differences
    if (existingUserIds.size !== selectedUserIds.size) return true;

    for (const userId of selectedUserIds) {
      if (!existingUserIds.has(userId)) return true;
    }
    for (const userId of existingUserIds) {
      if (!selectedUserIds.has(userId)) return true;
    }

    return false;
  }, [existingAccess, selectedUsers]);

  const handleShare = () => {
    if (!report) return;

    // Existing access user IDs
    const existingUserIds = new Set(
      existingAccess.map(access =>
        `${access.user_id}_${access.country_code}_${access.phone_number}`
      )
    );

    // Currently selected user IDs
    const selectedUserIds = new Set(selectedUsers);

    // Users to add (selected but not in existing)
    const usersToAdd = selectedUsers.filter(userId => !existingUserIds.has(userId));

    // Users to revoke (in existing but not selected)
    const usersToRevoke = Array.from(existingUserIds).filter(userId => !selectedUserIds.has(userId));

    // Only call if there are changes
    if (usersToAdd.length > 0 || usersToRevoke.length > 0) {
      onShare(report.id, usersToAdd, usersToRevoke);
      handleClose();
    } else {
      // No changes, just close
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedUsers([]);
    onClose();
  };

  if (!report) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Share "${report.title}"`}
      size="lg"
      footer={
        <HStack spacing={3}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleShare}
            isLoading={isSharing}
            loadingText="Saving..."
            isDisabled={!hasChanges}
          >
            Save Changes
          </Button>
        </HStack>
      }
    >
      {isLoadingAccess ? (
        <Center py={8}>
          <Spinner size="lg" color="brand.500" />
        </Center>
      ) : (
        <VStack spacing={4} align="stretch">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <MdSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Box
            maxH="400px"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="md"
            p={3}
          >
            {filteredMembers.length === 0 ? (
            <EmptyState
              title="No team members found"
              description="Try adjusting your search"
            />
          ) : (
            <CheckboxGroup
              value={selectedUsers}
              onChange={(values) => setSelectedUsers(values as string[])}
            >
              <VStack spacing={2} align="stretch">
                {filteredMembers.map((member) => (
                  <Checkbox key={member.id} value={`${member.id}_${member.country_code}_${member.phone_number}`}>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{member.name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {member.country_code} {member.phone_number}
                      </Text>
                    </VStack>
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          )}
          </Box>
        </VStack>
      )}
    </Modal>
  );
}
